/* eslint-disable @typescript-eslint/no-explicit-any */
/* Three-way merge of report definitions, so two people editing the same draft
 * keep each other's work instead of one save being rejected.
 *
 *   base   = the draft as this tab last saw it on the server
 *   mine   = what this tab has now (base + local edits)
 *   theirs = the draft on the server now (base + other people's saves)
 *
 * Objects merge key by key. Arrays whose items all carry a string `id` (pages,
 * widgets, ...) merge item by item: additions from either side are kept, a
 * deletion wins only over an item the other side left untouched, and an item
 * edited by one side and deleted by the other is kept. Anything else (a plain
 * array, a scalar) that both sides changed differently is a true conflict:
 * the local value wins and the path is reported. */

export interface MergeResult<T> {
  merged: T;
  /** Paths where both sides changed the same value differently (mine kept). */
  conflicts: string[];
}

function stable(x: any): string {
  if (x === undefined) return "\u0000undefined";
  if (x === null || typeof x !== "object") return JSON.stringify(x);
  if (Array.isArray(x)) return `[${x.map(stable).join(",")}]`;
  return `{${Object.keys(x)
    .sort()
    .map((k) => `${JSON.stringify(k)}:${stable(x[k])}`)
    .join(",")}}`;
}
export const same = (a: any, b: any): boolean => stable(a) === stable(b);

const isObj = (x: any): x is Record<string, any> => x !== null && typeof x === "object" && !Array.isArray(x);
const isIdArray = (x: any): x is Array<{ id: string }> => Array.isArray(x) && x.every((i) => isObj(i) && typeof i.id === "string");

function merge(base: any, mine: any, theirs: any, path: string, conflicts: string[]): any {
  if (same(mine, theirs)) return mine;
  if (same(base, mine)) return theirs;
  if (same(base, theirs)) return mine;

  if (isObj(mine) && isObj(theirs)) {
    const b = isObj(base) ? base : {};
    const out: Record<string, any> = {};
    const keys = new Set([...Object.keys(mine), ...Object.keys(theirs), ...Object.keys(b)]);
    for (const k of keys) {
      const v = merge(b[k], mine[k], theirs[k], path ? `${path}.${k}` : k, conflicts);
      if (v !== undefined) out[k] = v;
    }
    return out;
  }

  if (isIdArray(mine) && isIdArray(theirs) && (base === undefined || isIdArray(base))) {
    return mergeIdArrays((base as any[]) ?? [], mine, theirs, path, conflicts);
  }

  conflicts.push(path || "(root)");
  return mine;
}

function mergeIdArrays(base: any[], mine: any[], theirs: any[], path: string, conflicts: string[]): any[] {
  const byId = (a: any[]) => new Map(a.map((i) => [i.id as string, i]));
  const b = byId(base);
  const m = byId(mine);
  const t = byId(theirs);

  const keep = (id: string): any | undefined => {
    const inB = b.has(id);
    const inM = m.has(id);
    const inT = t.has(id);
    if (inM && inT) return merge(b.get(id), m.get(id), t.get(id), `${path}[${id}]`, conflicts);
    if (inM) {
      if (!inB) return m.get(id); // I added it
      if (same(b.get(id), m.get(id))) return undefined; // they deleted what I never touched
      conflicts.push(`${path}[${id}] (they deleted it, you edited it -- kept)`);
      return m.get(id);
    }
    if (inT) {
      if (!inB) return t.get(id); // they added it
      if (same(b.get(id), t.get(id))) return undefined; // I deleted what they never touched
      conflicts.push(`${path}[${id}] (you deleted it, they edited it -- kept)`);
      return t.get(id);
    }
    return undefined;
  };

  // Order: follow whoever actually reordered; theirs if both did.
  const baseOrder = base.map((i) => i.id);
  const theirsCommon = theirs.map((i) => i.id).filter((id) => b.has(id));
  const theirsReordered = !same(theirsCommon, baseOrder.filter((id) => t.has(id)));
  const primary = theirsReordered ? theirs : mine;
  const secondary = primary === theirs ? mine : theirs;
  const order: string[] = [];
  for (const i of [...primary, ...secondary]) if (!order.includes(i.id)) order.push(i.id);

  const out: any[] = [];
  for (const id of order) {
    const v = keep(id);
    if (v !== undefined) out.push(v);
  }
  return out;
}

export function merge3<T>(base: T, mine: T, theirs: T): MergeResult<T> {
  const conflicts: string[] = [];
  const merged = merge(base, mine, theirs, "", conflicts) as T;
  return { merged, conflicts };
}
